/**
 * Favorites Context
 * Single Responsibility: share the favorited-post set across the app
 * without prop drilling. The underlying state machine still lives in
 * useFavorites; this provider simply hoists the hook's lifecycle to
 * the app root so the set is read once per session, not once per page.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { STORAGE_KEYS } from '../services/storageKeys'
import { safeStorage } from '../services/safeStorage'
import { STORAGE_WRITE_DEBOUNCE_MS } from '../constants/displayLimits'
import { isStringArray } from '../utils/validation'

interface FavoritesAPI {
  favorites: string[]
  toggleFavorite: (postId: string) => void
  isFavorite: (postId: string) => boolean
  isLoaded: boolean
  count: number
}

export type { FavoritesAPI }

const FavoritesContext = createContext<FavoritesAPI | null>(null)

interface FavoritesProviderProps {
  children: ReactNode
}

export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set())
  const [isLoaded, setIsLoaded] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const result = safeStorage.readJSON<string[]>(STORAGE_KEYS.FAVORITES, isStringArray)
    if (result.ok) {
      latestRef.current = new Set(result.value)
      setFavorites(new Set(result.value))
    }
    setIsLoaded(true)
  }, [])

  useEffect(() => {
    if (!isLoaded) return
    latestRef.current = favorites

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      const snapshot = Array.from(latestRef.current)
      safeStorage.writeJSON(STORAGE_KEYS.FAVORITES, snapshot)
    }, STORAGE_WRITE_DEBOUNCE_MS)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        safeStorage.writeJSON(STORAGE_KEYS.FAVORITES, Array.from(latestRef.current))
      }
    }
  }, [favorites, isLoaded])

  const toggleFavorite = useCallback((postId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev)
      if (next.has(postId)) {
        next.delete(postId)
      } else {
        next.add(postId)
      }
      return next
    })
  }, [])

  const isFavorite = useCallback((postId: string) => favorites.has(postId), [favorites])

  const value = useMemo<FavoritesAPI>(
    () => ({
      favorites: Array.from(favorites),
      toggleFavorite,
      isFavorite,
      isLoaded,
      count: favorites.size,
    }),
    [favorites, toggleFavorite, isFavorite, isLoaded]
  )

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
}

export function useFavoritesContext(): FavoritesAPI {
  const ctx = useContext(FavoritesContext)
  if (!ctx) {
    throw new Error('useFavoritesContext must be used inside a <FavoritesProvider>')
  }
  return ctx
}
